// Sección "Marketing" de la ficha de contacto: estado + puntaje (lead scoring) editables y
// línea de tiempo unificada (eventos de marketing + notas, tareas, llamadas, reuniones, emails, visitas).
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar, CheckSquare, ClipboardList, FileText, Globe, ListPlus, type LucideIcon, Mail, MailOpen, MousePointerClick,
  Phone, Sparkles, StickyNote, Tag, TrendingUp, Workflow,
} from "lucide-react";
import { api } from "../../../lib/api.ts";
import { formatDate, formatDateTime } from "../../../lib/format.ts";
import { Button, Card, Input, Skeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { errMsg, relTime, StatusBadge, useMktOptions } from "./shared.tsx";

interface Item {
  id: string; cat: string; type: string; at: string | null; title: string | null; body: string | null;
  data: Record<string, unknown> | null; refName: string | null; userName: string | null;
}
interface Timeline {
  contact: { id: string; name: string; mktScore: number; mktStatus: string | null; canEdit: boolean };
  items: Item[]; hasMore: boolean;
}

const FILTERS = [
  { k: "all", l: "Todos" }, { k: "notes", l: "Notas" }, { k: "emails", l: "Emails" }, { k: "calls", l: "Llamadas" },
  { k: "tasks", l: "Tareas" }, { k: "meetings", l: "Reuniones" }, { k: "web", l: "Visitas web" }, { k: "marketing", l: "Marketing" },
];

const EVENT: Record<string, { label: string; icon: LucideIcon }> = {
  campaign_sent: { label: "Campaña enviada", icon: Mail },
  email_open: { label: "Abrió el email", icon: MailOpen },
  email_click: { label: "Hizo clic en el email", icon: MousePointerClick },
  unsubscribe: { label: "Se dio de baja", icon: Mail },
  bounce: { label: "Email rebotado", icon: Mail },
  form_submit: { label: "Envió un formulario", icon: FileText },
  landing_visit: { label: "Visitó una landing page", icon: Globe },
  page_view: { label: "Visitó una landing page", icon: Globe },
  survey_response: { label: "Respondió una encuesta", icon: ClipboardList },
  fb_lead: { label: "Lead de Facebook", icon: Sparkles },
  score: { label: "Actualización de puntos de scoring", icon: TrendingUp },
  flow_step: { label: "Paso de automatización", icon: Workflow },
  list_added: { label: "Añadido a lista", icon: ListPlus },
  status_change: { label: "Cambio de estado", icon: Tag },
  note: { label: "Nota", icon: StickyNote },
  task: { label: "Tarea", icon: CheckSquare },
  call: { label: "Llamada", icon: Phone },
  meeting: { label: "Reunión", icon: Calendar },
  email: { label: "Email", icon: Mail },
};

const str = (v: unknown): string => (v == null ? "" : String(v));

/** Línea de detalle de un item según su tipo. */
function detail(it: Item): string {
  const d = it.data ?? {};
  switch (it.type) {
    case "score": {
      const delta = Number(d["delta"] ?? 0);
      return `${delta > 0 ? "+" : ""}${delta} pts · Total ${str(d["total"])}${d["reason"] ? ` · ${str(d["reason"])}` : ""}`;
    }
    case "status_change": return `${str(d["from"]) || "Sin estado"} → ${str(d["to"]) || "Sin estado"}`;
    case "task": return [str(d["status"]), d["dueDate"] ? `vence ${formatDate(str(d["dueDate"]).slice(0, 10))}` : "", str(d["priority"])].filter(Boolean).join(" · ");
    case "call": return [str(d["callType"]), str(d["status"]), d["duration"] ? `${str(d["duration"])} min` : "", str(d["result"])].filter(Boolean).join(" · ");
    case "meeting": return [str(d["location"]), d["online"] ? "en línea" : ""].filter(Boolean).join(" · ");
    case "email": return [str(d["to"]), str(d["status"])].filter(Boolean).join(" · ");
    case "page_view": return [str(d["device"]), str(d["referrer"])].filter(Boolean).join(" · ");
    case "email_click": return str(d["url"]);
    default: return "";
  }
}

export function ContactMarketingTimeline({ contactId }: { contactId: string }): JSX.Element {
  const qc = useQueryClient();
  const opts = useMktOptions();
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(30);
  useEffect(() => setLimit(30), [filter]);

  const tl = useQuery<Timeline>({
    queryKey: ["mkt", "timeline", contactId, filter, limit],
    queryFn: () => api.get(`/mkt/contacts/${contactId}/timeline?type=${filter}&limit=${limit}`),
  });
  const c = tl.data?.contact;
  const [status, setStatus] = useState("");
  const [score, setScore] = useState("");
  useEffect(() => { if (c) { setStatus(c.mktStatus ?? ""); setScore(String(c.mktScore)); } }, [c]);

  const save = useMutation({
    mutationFn: (body: { mktStatus?: string | null; mktScore?: number }) => api.patch(`/mkt/contacts/${contactId}`, body),
    onSuccess: () => { toast.success("Marketing actualizado."); void qc.invalidateQueries({ queryKey: ["mkt"] }); },
    onError: (e) => toast.error(errMsg(e, "No se pudo guardar.")),
  });
  const dirty = c && (status.trim() !== (c.mktStatus ?? "") || score !== String(c.mktScore));
  const submit = (): void => {
    if (!c) return;
    const body: { mktStatus?: string | null; mktScore?: number } = {};
    if (status.trim() !== (c.mktStatus ?? "")) body.mktStatus = status.trim() || null;
    const n = Number(score);
    if (score !== String(c.mktScore)) {
      if (!Number.isInteger(n)) { toast.error("El puntaje debe ser un número entero."); return; }
      body.mktScore = n;
    }
    save.mutate(body);
  };

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-line">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg grid place-items-center bg-primary/[0.08] text-primary dark:text-ink shrink-0"><Sparkles size={16} strokeWidth={1.75} /></span>
          <h3 className="sec-title">Marketing</h3>
        </div>
        {c && (
          <div className="flex items-center gap-3 text-sm">
            <StatusBadge status={c.mktStatus} />
            <span className="inline-flex items-center gap-1 font-semibold text-ink tnum" title="Puntos de lead scoring"><TrendingUp size={14} className="text-ink-muted" />{c.mktScore} pts</span>
          </div>
        )}
      </div>

      {c?.canEdit && (
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <label className="flex-1 min-w-44">
            <span className="label">Estado</span>
            <Input list={`mkt-st-${contactId}`} maxLength={40} value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Lead frío, Cliente…" />
            <datalist id={`mkt-st-${contactId}`}>{(opts.data?.statuses ?? []).map((s) => <option key={s} value={s} />)}</datalist>
          </label>
          <label className="w-28">
            <span className="label">Puntaje</span>
            <Input type="number" step={1} value={score} onChange={(e) => setScore(e.target.value)} />
          </label>
          <Button variant="primary" disabled={!dirty || save.isPending} onClick={submit}>Guardar</Button>
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto scrollbar-thin mb-3" role="group" aria-label="Filtrar línea de tiempo">
        {FILTERS.map((f) => (
          <button key={f.k} type="button" aria-pressed={filter === f.k} onClick={() => setFilter(f.k)}
            className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap border transition-colors ${filter === f.k ? "bg-primary-soft border-primary text-primary dark:text-ink" : "border-line text-ink-secondary hover:bg-surface-hover"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {tl.isLoading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : tl.isError ? (
        <p className="text-sm text-danger">{errMsg(tl.error, "No se pudo cargar la línea de tiempo.")}</p>
      ) : (tl.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-ink-muted py-4 text-center">Sin actividad registrada.</p>
      ) : (
        <ol className="relative border-l border-line ml-3 space-y-4">
          {tl.data!.items.map((it) => {
            const ev = EVENT[it.type] ?? { label: it.type, icon: Sparkles };
            const Icon = ev.icon;
            const det = detail(it);
            return (
              <li key={it.id} className="ml-5 relative">
                <span className="absolute -left-8 top-0 w-6 h-6 rounded-full grid place-items-center bg-surface border border-line text-ink-secondary">
                  <Icon size={12} strokeWidth={2} />
                </span>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <p className="text-sm text-ink">
                    <span className="font-medium">{ev.label}</span>
                    {(it.title || it.refName) && <span className="text-ink-secondary">: {it.title ?? it.refName}</span>}
                  </p>
                  <time className="text-xs text-ink-muted whitespace-nowrap" dateTime={it.at ?? undefined} title={formatDateTime(it.at)}>{relTime(it.at)}</time>
                </div>
                {det && <p className={`text-xs mt-0.5 ${it.type === "score" ? "font-medium text-ink-secondary tnum" : "text-ink-muted"}`}>{det}</p>}
                {it.body && <p className="text-xs text-ink-secondary mt-1 whitespace-pre-wrap line-clamp-4">{it.body}</p>}
                {it.userName && <p className="text-[11px] text-ink-muted mt-0.5">{it.userName}</p>}
              </li>
            );
          })}
        </ol>
      )}
      {tl.data?.hasMore && (
        <div className="mt-4 text-center"><Button small onClick={() => setLimit((l) => Math.min(300, l + 30))} disabled={tl.isFetching}>Cargar más</Button></div>
      )}
    </Card>
  );
}
